import {
  STATIONS,
  athletesForLeg,
  fadedRunPaces,
  formatDuration,
  unbalancedRelayAthletes,
  stationWeightLabel,
  type Plan,
  type StationTarget,
} from "@roxpacer/core";
import { useState, type ReactNode } from "react";
import { useEstimate } from "../lib/use-estimate";
import { useActivePlan, usePlans } from "../store";
import { DurationInput } from "./DurationInput";

const FADE_OPTIONS = [0, 1, 2, 3, 4, 5];

function RunsToolbar() {
  const plan = useActivePlan();
  const updateActive = usePlans((s) => s.updateActive);
  const [pace, setPace] = useState(plan.runs[0]?.paceSecPerKm ?? 330);
  const [fade, setFade] = useState(0);

  return (
    <div className="card flex flex-wrap items-center gap-x-3 gap-y-2 p-3 text-sm">
      <span className="font-medium">Set all runs</span>
      <DurationInput label="Pace for all runs" value={pace} onCommit={setPace} suffix="/km" />
      <label className="flex items-center gap-1.5 text-ink-2">
        slowing
        <select
          className="field h-9 pr-1"
          value={fade}
          onChange={(e) => setFade(Number(e.target.value))}
          aria-label="Slowdown per run"
        >
          {FADE_OPTIONS.map((pct) => (
            <option key={pct} value={pct}>
              {pct}%
            </option>
          ))}
        </select>
        per run
      </label>
      <button
        type="button"
        className="btn ml-auto"
        onClick={() =>
          updateActive((p) => ({
            ...p,
            runs: fadedRunPaces(pace, fade).map((paceSecPerKm) => ({ paceSecPerKm })),
          }))
        }
      >
        Apply
      </button>
    </div>
  );
}

function SplitSlider({ plan, index }: { plan: Plan; index: number }) {
  const updateActive = usePlans((s) => s.updateActive);
  const [a, b] = plan.athletes;
  const station = plan.stations[index]!;
  if (!a || !b) return null;
  const aPct = station.split?.find((s) => s.athleteId === a.id)?.pct ?? 50;

  const setSplit = (pct: number) =>
    updateActive((p) => ({
      ...p,
      stations: p.stations.map((s, i) =>
        i === index
          ? {
              ...s,
              split: [
                { athleteId: a.id, pct },
                { athleteId: b.id, pct: 100 - pct },
              ],
            }
          : s,
      ),
    }));

  return (
    <div className="flex items-center gap-2 text-xs text-ink-2 sm:col-span-4">
      <span className="w-24 truncate text-right" title={a.name}>
        {a.name} <span className="tabular-nums text-ink">{Math.round(aPct)}%</span>
      </span>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={aPct}
        onChange={(e) => setSplit(Number(e.target.value))}
        aria-label={`${STATIONS[index]!.name} work split`}
        aria-valuetext={`${a.name} ${Math.round(aPct)}%, ${b.name} ${Math.round(100 - aPct)}%`}
        className="h-1.5 flex-1 accent-accent"
      />
      <span className="w-24 truncate" title={b.name}>
        <span className="tabular-nums text-ink">{Math.round(100 - aPct)}%</span> {b.name}
      </span>
    </div>
  );
}

export function LegEditor() {
  const plan = useActivePlan();
  const estimate = useEstimate(plan);
  const updateActive = usePlans((s) => s.updateActive);

  const setStation = (index: number, change: Partial<StationTarget>) =>
    updateActive((p) => ({
      ...p,
      stations: p.stations.map((s, i) => (i === index ? { ...s, ...change } : s)),
    }));
  const setRun = (index: number, paceSecPerKm: number) =>
    updateActive((p) => ({
      ...p,
      runs: p.runs.map((r, i) => (i === index ? { paceSecPerKm } : r)),
    }));

  // Relay leg picks stay a draft until every athlete has two legs, as the plan
  // only holds balanced legs.
  const [legDraft, setLegDraft] = useState<{ planId: string; athleteIds: string[] } | null>(null);
  const legAthleteIds =
    legDraft?.planId === plan.id && plan.format === "relay"
      ? legDraft.athleteIds
      : plan.stations.map((_, i) => athletesForLeg(plan, i)[0] ?? "");
  const unbalanced =
    plan.format === "relay" ? unbalancedRelayAthletes(plan.athletes, legAthleteIds) : [];
  const athleteName = (id: string) => {
    const n = plan.athletes.findIndex((a) => a.id === id);
    return plan.athletes[n]?.name.trim() || `Athlete ${n + 1}`;
  };
  const setLegAthlete = (index: number, athleteId: string) => {
    const athleteIds = legAthleteIds.map((id, i) => (i === index ? athleteId : id));
    if (unbalancedRelayAthletes(plan.athletes, athleteIds).length > 0) {
      setLegDraft({ planId: plan.id, athleteIds });
      return;
    }
    setLegDraft(null);
    updateActive((p) => ({
      ...p,
      stations: p.stations.map((s, i) => ({ ...s, athleteId: athleteIds[i] })),
    }));
  };

  return (
    <section className="space-y-3" aria-labelledby="legs-heading">
      <h2 id="legs-heading" className="text-sm font-semibold tracking-wide text-ink-2 uppercase">
        Race plan
      </h2>
      <RunsToolbar />
      {unbalanced.length > 0 && (
        <p role="alert" className="card border-danger! p-3 text-sm text-danger">
          Each athlete must do 2 legs:{" "}
          {unbalanced
            .map(({ athleteId, legs }) => `${athleteName(athleteId)} has ${legs}`)
            .join(", ")}
          . Leg changes are saved once the legs balance.
        </p>
      )}
      <ol className="space-y-2">
        {STATIONS.map((station, i) => {
          const leg = i + 1;
          const stationSegment = estimate.segments.find((s) => s.id === `station-${leg}`);
          const target = plan.stations[i]!;
          const weight = stationWeightLabel(station.id, plan);
          return (
            <li key={station.id} className="card p-3">
              <div className="mb-2.5 flex items-baseline gap-2">
                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-surface-2 text-xs font-semibold tabular-nums">
                  {leg}
                </span>
                <h3 className="font-semibold">{station.name}</h3>
                <span className="text-xs text-muted">
                  {station.amount}
                  {station.unit === "m" ? " m" : " reps"}
                  {weight && ` · ${weight}`}
                </span>
                {plan.format === "relay" && (
                  <select
                    className="field h-7 max-w-32 py-0 pr-1 text-xs aria-invalid:border-danger! aria-invalid:ring-2 aria-invalid:ring-danger/30"
                    aria-label={`Leg ${leg} athlete`}
                    aria-invalid={unbalanced.some((a) => a.athleteId === legAthleteIds[i])}
                    value={legAthleteIds[i]}
                    onChange={(e) => setLegAthlete(i, e.target.value)}
                  >
                    {plan.athletes.map((athlete) => (
                      <option key={athlete.id} value={athlete.id}>
                        {athleteName(athlete.id)}
                      </option>
                    ))}
                  </select>
                )}
                <span className="ml-auto text-xs text-muted">
                  at{" "}
                  <span className="font-medium text-ink tabular-nums">
                    {formatDuration(stationSegment?.endSec ?? 0)}
                  </span>
                </span>
              </div>
              <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 sm:grid-cols-4 sm:gap-x-4">
                <Field color="bg-run" label={`Run ${leg}`}>
                  <DurationInput
                    label={`Run ${leg} pace`}
                    suffix="/km"
                    max={3600}
                    value={plan.runs[i]!.paceSecPerKm}
                    onCommit={(sec) => setRun(i, sec)}
                  />
                </Field>
                <Field color="bg-roxzone" label="Roxzone in">
                  <DurationInput
                    label={`Roxzone in ${leg} time`}
                    allowZero
                    max={3600}
                    value={target.roxzoneInSec}
                    onCommit={(roxzoneInSec) => setStation(i, { roxzoneInSec })}
                  />
                </Field>
                <Field color="bg-station" label="Station">
                  <DurationInput
                    label={`${station.name} time`}
                    value={target.targetSec}
                    onCommit={(targetSec) => setStation(i, { targetSec })}
                  />
                </Field>
                <Field color="bg-roxzone" label="Roxzone out">
                  <DurationInput
                    label={`Roxzone out ${leg} time`}
                    allowZero
                    max={3600}
                    value={target.roxzoneOutSec}
                    onCommit={(roxzoneOutSec) => setStation(i, { roxzoneOutSec })}
                  />
                </Field>
                {plan.format === "doubles" && <SplitSlider plan={plan} index={i} />}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Field({ color, label, children }: { color: string; label: string; children: ReactNode }) {
  return (
    <div className="col-span-2 flex items-center justify-between gap-2 sm:col-span-1 sm:justify-start">
      <span className="flex items-center gap-1.5 text-sm text-ink-2">
        <span aria-hidden className={`h-3 w-1 rounded-full ${color}`} />
        {label}
      </span>
      {children}
    </div>
  );
}
